import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import AdminDeleteButton from "@/components/AdminDeleteButton";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, ArrowLeft, ChevronRight, XCircle, Edit2, FileText, ExternalLink } from "lucide-react";
import LineItemsEditor from "@/components/LineItemsEditor";
import AuditTrail from "@/components/AuditTrail";
import CommercialStatusBadge from "@/components/CommercialStatusBadge";
import CommercialChain from "@/components/CommercialChain";
import SendEmailDialog from "@/components/SendEmailDialog";
import { Mail } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const EMAIL_ROLES = new Set(["admin", "super_admin", "plant_head", "sales"]);

export default function InquiryDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lostDialog, setLostDialog] = useState(false);
  const [emailDialog, setEmailDialog] = useState(false);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  async function refresh() {
    setLoading(true);
    const { data } = await api.get(`/inquiries/${id}`);
    setD(data);
    setLoading(false);
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [id]);

  if (loading || !d) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-blue-900" /></div>;
  }

  async function markLost() {
    if (!reason.trim()) { toast.error("Reason required"); return; }
    setSaving(true);
    try {
      await api.post(`/inquiries/${id}/mark-lost`, { reason });
      toast.success("Inquiry marked as Lost");
      setLostDialog(false); setReason("");
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  }

  const canEdit = d.status === "Open" && ["sales", "plant_head", "admin", "super_admin"].includes(user.role);
  const canQuote = ["Open", "Quoted"].includes(d.status) && ["sales", "plant_head", "admin", "super_admin"].includes(user.role);
  const canMarkLost = d.status === "Open";

  return (
    <div data-testid="inquiry-detail-page" className="space-y-5 pb-6">
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <Link to="/inquiries" className="hover:text-slate-900 flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Inquiries
        </Link>
        <ChevronRight className="w-3 h-3" />
        <span className="font-mono text-slate-900">{d.inquiry_number}</span>
      </div>

      <Card className="border-slate-200 p-5">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-red-600 font-semibold">Inquiry · Stage 1 of 4</div>
            <div className="mt-1 font-mono text-xl md:text-2xl font-bold text-slate-900">{d.inquiry_number}</div>
            <div className="mt-2 flex items-center gap-3 text-sm">
              <span className="text-slate-600">{d.customer_name}</span>
              <span className="text-slate-300">·</span>
              <span className="text-slate-600">{d.plant_name}</span>
              <span className="text-slate-300">·</span>
              <span className="text-slate-500 font-mono text-xs">{d.inquiry_date}</span>
            </div>
          </div>
          <div className="flex flex-col md:items-end gap-3">
            <CommercialStatusBadge status={d.status} className="text-xs px-3 py-1" />
            <div className="flex flex-wrap gap-2">
              {canEdit && (
                <Button size="sm" variant="outline" data-testid="inq-edit-btn" onClick={() => nav(`/inquiries/${id}/edit`)}>
                  <Edit2 className="w-3.5 h-3.5 mr-1" /> Edit
                </Button>
              )}
              {canQuote && (
                <Button size="sm" data-testid="inq-quote-btn"
                  className="bg-blue-900 hover:bg-blue-800"
                  onClick={() => nav(`/quotations/new?inquiry_id=${id}`)}
                >
                  <FileText className="w-3.5 h-3.5 mr-1" /> Create Quotation
                </Button>
              )}
              {canMarkLost && (
                <Button size="sm" variant="outline" data-testid="inq-lost-btn"
                        onClick={() => setLostDialog(true)}
                        className="border-red-200 text-red-700 hover:bg-red-50">
                  <XCircle className="w-3.5 h-3.5 mr-1" /> Mark Lost
                </Button>
              )}
              {EMAIL_ROLES.has(user?.role) && (
                <Button size="sm" variant="outline" data-testid="inq-send-email-btn"
                        onClick={() => setEmailDialog(true)}>
                  <FileText className="w-3.5 h-3.5 mr-1" /> Send Email
                </Button>
              )}
              <AdminDeleteButton
                endpoint={`/inquiries/${id}`}
                entityName="Inquiry"
                entityNumber={d.inquiry_number}
                redirectAfter="/inquiries"
              />
            </div>
          </div>
        </div>
        {d.lost_reason && (
          <div className="mt-4 text-xs bg-red-50 border border-red-200 text-red-900 rounded px-3 py-2">
            <strong>Lost reason:</strong> {d.lost_reason}
          </div>
        )}
      </Card>

      <Card className="border-slate-200 p-4">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Commercial Chain</div>
        <CommercialChain type="inquiry" entity={d} />
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Card className="border-slate-200 lg:col-span-2 p-5">
          <div className="text-sm font-semibold text-slate-900 mb-4" style={{ fontFamily: "Chivo, sans-serif" }}>Cylinder Line Items</div>
          <LineItemsEditor value={d.line_items || []} onChange={() => {}} readOnly />
        </Card>

        <div className="space-y-5">
          <Card className="border-slate-200 p-5">
            <div className="text-sm font-semibold text-slate-900 mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>Contact & Source</div>
            <dl className="space-y-2 text-sm">
              <KV label="Contact" value={d.contact_person?.name} />
              {d.contact_person?.designation && <KV label="Designation" value={d.contact_person.designation} />}
              {d.contact_person?.phone && <KV label="Phone" value={d.contact_person.phone} mono />}
              {d.contact_person?.email && <KV label="Email" value={d.contact_person.email} mono />}
              <KV label="Source" value={d.source} />
              <KV label="Expected Delivery" value={d.expected_delivery} mono />
              <KV label="Sales Engineer" value={d.created_by_name} />
            </dl>
            {d.remarks && (
              <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-600 italic">
                {d.remarks}
              </div>
            )}
          </Card>

          {d.quotations?.length > 0 && (
            <Card className="border-slate-200 p-5">
              <div className="text-sm font-semibold text-slate-900 mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>Linked Quotations</div>
              <div className="space-y-2">
                {d.quotations.map((q) => (
                  <Link key={q.id} to={`/quotations/${q.id}`}
                        data-testid={`linked-quotation-${q.quotation_number}`}
                        className="flex items-center justify-between p-2 border border-slate-200 rounded hover:border-blue-400 hover:bg-blue-50/40">
                    <div>
                      <div className="font-mono text-xs font-semibold text-blue-900">{q.quotation_number}</div>
                      <div className="text-[11px] text-slate-500">Rev {q.current_revision} · ₹{(q.total_value || 0).toLocaleString("en-IN")}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <CommercialStatusBadge status={q.status} />
                      <ExternalLink className="w-3.5 h-3.5 text-slate-400" />
                    </div>
                  </Link>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>

      <Card className="border-slate-200 p-5">
        <div className="text-sm font-semibold text-slate-900 mb-4" style={{ fontFamily: "Chivo, sans-serif" }}>Audit Trail</div>
        <AuditTrail items={d.audit_trail || []} />
      </Card>

      <Dialog open={lostDialog} onOpenChange={setLostDialog}>
        <DialogContent data-testid="lost-dialog">
          <DialogHeader>
            <DialogTitle>Mark Inquiry as Lost</DialogTitle>
            <DialogDescription>Provide a reason. The inquiry will be closed.</DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-600">Reason *</Label>
            <Textarea data-testid="lost-reason" value={reason} rows={3}
                      onChange={(e) => setReason(e.target.value)} className="mt-1"
                      placeholder="e.g. Customer chose competitor on price" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLostDialog(false)}>Cancel</Button>
            <Button data-testid="lost-confirm" onClick={markLost} disabled={saving} className="bg-red-600 hover:bg-red-700">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Mark Lost"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SendEmailDialog
        open={emailDialog} onClose={() => setEmailDialog(false)}
        defaultSubject={d ? `Inquiry ${d.inquiry_number} — ${d.customer_name}` : ""}
        defaultBody={d ? `Dear Sir/Madam,\n\nReferring to inquiry ${d.inquiry_number} dated ${d.inquiry_date}.\n\nBest regards,\nHISPL Sales Team` : ""}
        relatedModule="inquiry" relatedRecordId={id} relatedRecordNumber={d?.inquiry_number}
      />
    </div>
  );
}

function KV({ label, value, mono }) {
  return (
    <div className="flex justify-between items-baseline gap-3">
      <dt className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</dt>
      <dd className={`text-right text-sm text-slate-800 ${mono ? "font-mono text-xs" : ""}`}>
        {value || <span className="text-slate-300">—</span>}
      </dd>
    </div>
  );
}
